import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiResponseError } from '@/lib/api-fetch'
import { getOperation } from '@/lib/api/client/operations'
import type { Operation, OperationItem, OperationListScope } from '@/lib/api/schemas/operations.schema'
import { isActiveOperation } from '@/lib/operations/labels'
import { operationsPoller } from '@/lib/operations/poller'

export { ACTIVE_POLL_MS, IDLE_POLL_MS, MAX_ACTIVE_POLL_MS } from '@/lib/operations/poller'

export const OPERATIONS_POLL_ERROR = 'Не удалось обновить операции. Повторим автоматически.'

type OperationListener = (operation: Operation) => void
const listeners = new Set<OperationListener>()

/** Hands a freshly enqueued operation to every mounted view and polls at once. */
export function announceOperation(operation: Operation): void {
  for (const listener of listeners) listener(operation)
  operationsPoller.refresh()
}

function upsert(list: readonly Operation[], operation: Operation): Operation[] {
  const index = list.findIndex((candidate) => candidate.id === operation.id)
  if (index === -1) return [operation, ...list]
  return list.map((candidate, position) => (position === index ? operation : candidate))
}

export type OperationItemsView = { operationId: string; items: OperationItem[] }

export type OperationsView = {
  operations: Operation[]
  finished: Operation[]
  items: OperationItemsView | null
  error: string | null
  loaded: boolean
  hasMore: boolean
  refresh: () => void
  dismissFinished: () => void
}

/**
 * One view of the tab's shared operations poller. With `trackFinished`, operations that
 * leave the active list are re-read by id once so their terminal state stays visible
 * until the viewer dismisses it.
 */
export function useOperations({
  scope,
  expandedId = null,
  trackFinished = false,
}: {
  scope: OperationListScope
  expandedId?: string | null
  trackFinished?: boolean
}): OperationsView {
  const [operations, setOperations] = useState<Operation[]>([])
  const [finished, setFinished] = useState<Operation[]>([])
  const [items, setItems] = useState<OperationItemsView | null>(null)
  const [listFailed, setListFailed] = useState(false)
  const [itemsFailed, setItemsFailed] = useState(false)
  const [settleFailed, setSettleFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const seenActiveRef = useRef(new Set<string>())
  const settlingRef = useRef(new Set<string>())

  const refresh = useCallback(() => operationsPoller.refresh(), [])

  useEffect(() => {
    const listener: OperationListener = (operation) => {
      if (scope === 'all' || isActiveOperation(operation)) setOperations((current) => upsert(current, operation))
      if (trackFinished && isActiveOperation(operation)) seenActiveRef.current.add(operation.id)
    }
    listeners.add(listener)
    return () => { listeners.delete(listener) }
  }, [scope, trackFinished])

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    const settleFinished = async (current: readonly Operation[]) => {
      const seen = seenActiveRef.current
      const activeIds = new Set(current.filter(isActiveOperation).map((operation) => operation.id))
      for (const id of activeIds) seen.add(id)
      const vanished = [...seen].filter((id) => !activeIds.has(id) && !settlingRef.current.has(id))
      if (vanished.length === 0) return
      for (const id of vanished) settlingRef.current.add(id)
      const results = await Promise.allSettled(vanished.map((id) => getOperation(id, signal)))
      for (const id of vanished) settlingRef.current.delete(id)
      if (signal.aborted) return
      const terminal: Operation[] = []
      let failed = false
      results.forEach((result, index) => {
        const id = vanished[index]
        if (id === undefined) return
        if (result.status === 'fulfilled') {
          if (!isActiveOperation(result.value)) {
            terminal.push(result.value)
            seen.delete(id)
          }
        } else if (result.reason instanceof ApiResponseError && result.reason.status === 404) {
          seen.delete(id)
        } else {
          failed = true
        }
      })
      if (terminal.length > 0) setFinished((list) => terminal.reduce(upsert, list))
      setSettleFailed(failed)
    }

    const unsubscribe = operationsPoller.subscribeList(scope, (snapshot) => {
      setOperations(snapshot.operations)
      setHasMore(snapshot.hasMore)
      setListFailed(snapshot.failed)
      setLoaded(true)
      if (trackFinished && !snapshot.failed) void settleFinished(snapshot.operations)
    })
    return () => {
      unsubscribe()
      controller.abort()
    }
  }, [scope, trackFinished])

  useEffect(() => {
    if (expandedId === null) return undefined
    return operationsPoller.subscribeItems(expandedId, (snapshot) => {
      setItemsFailed(snapshot.failed)
      if (snapshot.items !== null) setItems({ operationId: snapshot.operationId, items: snapshot.items })
    })
  }, [expandedId])

  const dismissFinished = useCallback(() => setFinished([]), [])
  const error = listFailed || itemsFailed || settleFailed ? OPERATIONS_POLL_ERROR : null

  return { operations, finished, items, error, loaded, hasMore, refresh, dismissFinished }
}

/** Follows one operation by id on the shared tick until the caller stops watching it. */
export function useWatchedOperation(operationId: string | null): Operation | null {
  const [operation, setOperation] = useState<Operation | null>(null)

  useEffect(() => {
    setOperation(null)
    if (operationId === null) return undefined
    return operationsPoller.watchOperation(operationId, (snapshot) => {
      if (snapshot.operation !== null) setOperation(snapshot.operation)
    })
  }, [operationId])

  return operationId === null ? null : operation
}
